import React, { useMemo } from "react";
import PropTypes from "prop-types";

/**
 * Pagination - reusable component
 *
 * Props:
 *  - totalItems (number) required
 *  - currentPage (number) required (1-based)
 *  - pageSize (number) required
 *  - onPageChange(page:number) required
 *  - onPageSizeChange(size:number) optional
 *  - pageSizeOptions (array) optional
 *  - siblingCount (number) optional - how many pages to show around current (default 1)
 *  - boundaryCount (number) optional - how many pages to show at start/end (default 1)
 *  - showJump (bool) optional - show "First/Last" buttons
 *  - className (string) optional - wrapper class
 *
 * This implements a simple truncation using "..." when page list is long.
 */
export default function Pagination({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  siblingCount = 1,
  boundaryCount = 1,
  showJump = true,
  className = "",
}) {
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / pageSize));

  // clamp current page
  const safeCurrent = Math.max(1, Math.min(currentPage, totalPages));

  const createRange = (start, end) => {
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };

  // compute the visible page numbers with truncation
  const paginationRange = useMemo(() => {
    const totalNumbers = siblingCount * 2 + boundaryCount * 2 + 3; // including current and 2 dots
    if (totalPages <= totalNumbers) {
      return createRange(1, totalPages);
    }

    const leftSibling = Math.max(safeCurrent - siblingCount, boundaryCount + 2);
    const rightSibling = Math.min(
      safeCurrent + siblingCount,
      totalPages - boundaryCount - 1
    );

    const pages = [];

    // left boundary
    pages.push(...createRange(1, boundaryCount));

    if (leftSibling > boundaryCount + 2) {
      pages.push("left-ellipsis");
    } else if (leftSibling === boundaryCount + 2) {
      pages.push(boundaryCount + 1);
    }

    // main middle range
    pages.push(...createRange(leftSibling, rightSibling));

    if (rightSibling < totalPages - boundaryCount - 1) {
      pages.push("right-ellipsis");
    } else if (rightSibling === totalPages - boundaryCount - 1) {
      pages.push(totalPages - boundaryCount);
    }

    // right boundary
    pages.push(...createRange(totalPages - boundaryCount + 1, totalPages));

    return pages;
  }, [totalPages, safeCurrent, siblingCount, boundaryCount]);

  const goTo = (p) => {
    const page = Math.max(1, Math.min(p, totalPages));
    if (page === safeCurrent) return;
    onPageChange(page);
  };

  return (
    <div className={`pagination-wrapper ${className}`}>
      {showJump && (
        <button
          className="tiny-btn outline"
          onClick={() => goTo(1)}
          disabled={safeCurrent === 1}
        >
          {"<<"}
        </button>
      )}
      <button
        className="tiny-btn outline"
        onClick={() => goTo(safeCurrent - 1)}
        disabled={safeCurrent === 1}
      >
        {"<"}
      </button>

      {paginationRange.map((p, i) =>
        typeof p === "number" ? (
          <button
            key={p}
            className={`tiny-btn ${p === safeCurrent ? "active" : ""}`}
            onClick={() => goTo(p)}
            aria-current={p === safeCurrent ? "page" : undefined}
          >
            {p}
          </button>
        ) : (
          <span key={`ell-${i}`} className="tiny-btn disabled">
            …
          </span>
        )
      )}

      <button
        className="tiny-btn outline"
        onClick={() => goTo(safeCurrent + 1)}
        disabled={safeCurrent === totalPages}
      >
        {">"}
      </button>
      {showJump && (
        <button
          className="tiny-btn outline"
          onClick={() => goTo(totalPages)}
          disabled={safeCurrent === totalPages}
        >
          {">>"}
        </button>
      )}

      {typeof onPageSizeChange === "function" && (
        <div
          style={{
            marginLeft: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <label style={{ color: "var(--muted)", fontSize: 12 }}>PerPage</label>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="select"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

Pagination.propTypes = {
  totalItems: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.array,
  siblingCount: PropTypes.number,
  boundaryCount: PropTypes.number,
  showJump: PropTypes.bool,
  className: PropTypes.string,
};
